import $ from 'jquery';

import Payment from 'app/src/data/models/Payment';
import PaymentTransaction from 'app/src/data/models/PaymentTransaction';
import Localize from 'app/src/localize/Localize';
import PaymentServiceTerminal from 'app/src/payments/terminals/PaymentServiceTerminal';
import Terminal, { TerminalTransactionResponse } from 'app/src/payments/terminals/Terminal';
import TyroTerminalService from 'app/src/payments/terminals/TyroTerminalService';

const _DESCRIPTION = 'Tyro Payment Terminal';

export default class TyroTerminal extends PaymentServiceTerminal {
  /**
   * Constructor for a TyrorTerminal object.
   *
   * @param terminalModel {CCTerminal}    The API model representation of the terminal
   */
  constructor(terminalModel) {
    super(terminalModel);

    this._usesProprietaryUI = true;
  }

  /**
   * Retrieves a descriptive string about the terminal type.
   *
   * @returns {string}    The string identifying the terminal type
   */
  description() {
    return _DESCRIPTION;
  }

  /**
   * Communicates with the terminal to start a transaction.
   *
   * @param {Object} paymentOptions
   * @param {Decimal} paymentOptions.amount  The amount to transact with the terminal.
   * @param {Number} paymentOptions.saleID   The ID of the sale to which the transaction will be associated
   * @param {GatewayConfig} paymentOptions.gatewayConfig  The gateway configuration used to transact the request
   */
  processPayment(paymentOptions) {
    const { amount, saleID } = paymentOptions;
    let deferred = $.Deferred();

    this.stagePayment(paymentOptions)
      .done(({ terminalPayment, paymentTransaction }) => {
        paymentTransaction.gatewayRequest = {
          amount: amount.abs().times(100).toFixed(0), // Tyro expects amount in cents
          transactionId: saleID,
          integratedReceipt: false,
          mid: this.model.configuration.merchantID,
          tid: this.model.configuration.terminalID,
          integrationKey: this.model.configuration.integrationKey,
          method: amount.isNegative()
            ? PaymentTransaction.OperationType.REFUND
            : PaymentTransaction.OperationType.SALE,
        };

        TyroTerminalService.getInstance().done((terminalService) => {
          if (terminalService) {
            terminalService.startTransaction(amount, this.model).always((response) => {
              paymentTransaction.gatewayResponse = response;
              terminalPayment.rawResponse = paymentTransaction;

              switch (response.result) {
                case TyroTerminalService.TransactionStatus.APPROVED:
                  deferred.resolve(
                    new TerminalTransactionResponse(
                      Terminal.Response.APPROVED,
                      response.result,
                      terminalPayment
                    )
                  );
                  break;

                case TyroTerminalService.TransactionStatus.DECLINED:
                  deferred.reject(
                    new TerminalTransactionResponse(
                      Terminal.Response.DECLINED,
                      response.result,
                      terminalPayment
                    )
                  );
                  break;

                case TyroTerminalService.TransactionStatus.SYSTEM_ERROR:
                  deferred.reject(
                    new TerminalTransactionResponse(
                      Terminal.Response.TERMINAL_ERROR,
                      response.result,
                      terminalPayment
                    )
                  );
                  break;

                case TyroTerminalService.TransactionStatus.NOT_STARTED:
                  deferred.reject(
                    new TerminalTransactionResponse(
                      Terminal.Response.COULD_NOT_START_TERMINAL,
                      response.result,
                      terminalPayment
                    )
                  );
                  break;

                case TyroTerminalService.TransactionStatus.CANCELLED:
                  deferred.reject(
                    new TerminalTransactionResponse(
                      Terminal.Response.CANCELLED_FROM_TERMINAL,
                      response.result,
                      terminalPayment
                    )
                  );
                  break;

                default:
                  deferred.reject(
                    new TerminalTransactionResponse(
                      Terminal.Response.TERMINAL_ERROR,
                      response.result,
                      terminalPayment
                    )
                  );
                  break;
              }
            });
          } else {
            deferred.reject(
              new TerminalTransactionResponse(
                Terminal.Response.ERROR,
                Localize.translate('An unknown system error has occurred')
              )
            );
          }
        });
      })
      .fail((transactionResponse) => {
        deferred.reject(transactionResponse);
      });

    return deferred.promise();
  }

  /**
   * Starts a terminal pairing operation
   *
   * @returns {jQuery.promise}
   */
  pair() {
    let deferred = $.Deferred();

    TyroTerminalService.getInstance().done((terminalService) => {
      if (terminalService) {
        terminalService
          .pair(this.model.configuration.merchantID, this.model.configuration.terminalID)
          .done((integrationKey) => {
            deferred.resolve(integrationKey);
          })
          .fail((errorMessage) => {
            deferred.reject(errorMessage);
          })
          .progress((progressMessage) => {
            deferred.notify(progressMessage);
          });
      } else {
        deferred.reject(Localize.translate('Unable to contact the Tyro servers.'));
      }
    });

    return deferred.promise();
  }

  /**
   * Cancels a pairing operation.
   */
  cancelPairing() {
    TyroTerminalService.getInstance().done((terminalService) => {
      if (terminalService) {
        terminalService.cancelPairing();
      }
    });
  }
}
